import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { siteDiaryAPI, roomsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Camera, Calendar, Trash2, Loader2, DoorOpen, ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function SiteDiary({ projectId: propProjectId }) {
  const params = useParams();
  const projectId = propProjectId || params.projectId;
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [entries, setEntries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [roomFilter, setRoomFilter] = useState('all');
  const [viewImage, setViewImage] = useState(null);

  const emptyForm = { title: '', notes: '', date: new Date().toISOString().split('T')[0], room_id: '', phase: '', photos: [''] };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (roomFilter !== 'all') params.room_id = roomFilter;
      const [entriesRes, roomsRes] = await Promise.all([
        siteDiaryAPI.list(projectId, params),
        roomsAPI.list(projectId),
      ]);
      setEntries(entriesRes.data);
      setRooms(roomsRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [projectId, roomFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = { ...form, photos: form.photos.filter(p => p.trim()) };
      await siteDiaryAPI.create(projectId, payload);
      fetchData();
      setDialogOpen(false);
      setForm(emptyForm);
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (entryId) => {
    try { await siteDiaryAPI.delete(entryId); fetchData(); } catch (e) { console.error(e); }
  };

  const addPhotoField = () => setForm({ ...form, photos: [...form.photos, ''] });
  const updatePhoto = (idx, val) => { const photos = [...form.photos]; photos[idx] = val; setForm({ ...form, photos }); };
  const removePhoto = (idx) => setForm({ ...form, photos: form.photos.filter((_, i) => i !== idx) });

  const getRoomName = (roomId) => rooms.find(r => r.room_id === roomId)?.name || '';

  if (loading) return <div className="animate-pulse"><div className="h-48 bg-muted rounded" /></div>;

  return (
    <div className="space-y-4" data-testid="site-diary">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium">Site Diary / Photo Progress</h3>
          <p className="text-xs text-muted-foreground">{entries.length} entries</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={roomFilter} onValueChange={setRoomFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="diary-room-filter"><SelectValue placeholder="All Rooms" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Rooms</SelectItem>{rooms.map(r => <SelectItem key={r.room_id} value={r.room_id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
          {canEdit && (
            <Button onClick={() => { setForm(emptyForm); setDialogOpen(true); }} data-testid="add-diary-entry-btn">
              <Plus className="h-4 w-4 mr-2" />Add Entry
            </Button>
          )}
        </div>
      </div>

      {entries.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Camera className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No progress entries yet</p><p className="text-xs text-muted-foreground mt-1">Document site progress with photos and notes</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {entries.map(entry => (
            <Card key={entry.entry_id} data-testid={`diary-entry-${entry.entry_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-sm">{entry.title || 'Site Update'}</h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{entry.date}</span>
                      {entry.room_id && <span className="flex items-center gap-1"><DoorOpen className="h-3 w-3" />{getRoomName(entry.room_id)}</span>}
                      {entry.phase && <span className="px-1.5 py-0.5 bg-muted rounded text-[10px]">{entry.phase}</span>}
                      <span>{entry.created_by_name}</span>
                    </div>
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(entry.entry_id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {entry.notes && <p className="text-sm text-muted-foreground mb-3">{entry.notes}</p>}
                {entry.photos?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {entry.photos.filter(p => p).map((photo, idx) => (
                      <button key={idx} onClick={() => setViewImage(photo)} className="rounded overflow-hidden border hover:opacity-80 transition-opacity">
                        <img src={photo} alt={`Progress ${idx + 1}`} className="h-20 w-20 sm:h-24 sm:w-24 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Progress Entry</DialogTitle><DialogDescription>Document site progress with photos and notes</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Kitchen tiling done" required data-testid="diary-title-input" /></div>
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Room</Label>
                  <Select value={form.room_id || 'none'} onValueChange={v => setForm({ ...form, room_id: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">General</SelectItem>{rooms.map(r => <SelectItem key={r.room_id} value={r.room_id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Phase</Label><Input value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })} placeholder="e.g., Execution" /></div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Progress details..." /></div>
              <div className="space-y-2">
                <Label>Photos (paste image URLs)</Label>
                {form.photos.map((photo, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input value={photo} onChange={e => updatePhoto(idx, e.target.value)} placeholder="https://..." className="flex-1" data-testid={`diary-photo-${idx}`} />
                    {form.photos.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => removePhoto(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addPhotoField}><ImageIcon className="h-3.5 w-3.5 mr-1" />Add Photo URL</Button>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-diary-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Entry</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Viewer */}
      <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
        <DialogContent className="sm:max-w-2xl"><img src={viewImage} alt="Site progress" className="w-full rounded" /></DialogContent>
      </Dialog>
    </div>
  );
}
